import {useEffect, useRef, RefObject} from 'react';

export type UseResizeObserverOptions = {
  debounce?: number;
  onResize?: () => void;
};

/**
 * Hook that observes an element's size changes and calls a callback with debounce
 * @param elementRef - Reference to the element to observe
 * @param onResize - Callback function to call when resize occurs
 * @param debounce - Debounce delay in ms (default: 100)
 */
export function useResizeObserver(
  elementRef: RefObject<HTMLElement>,
  onResize: () => void,
  debounce = 100
) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => {
      const element = elementRef.current;
      if (!element) return;

      const resizeObserver = new ResizeObserver(() => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(onResize, debounce);
      });

      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      elementRef,
      debounce,
      // onResize
    ]
  );
}
